import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  Snackbar,
  Tooltip,
  Slider,
  Switch,
  FormControlLabel,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  RestartAlt as ResetIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
  MyLocation as MyLocationIcon,
  Save as SaveIcon,
  Router as RouterIcon,
} from '@mui/icons-material';

import { sensorsService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

function Sensors() {
  const { isAdmin } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [sensors, setSensors] = useState([]);
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editSensorDialogOpen, setEditSensorDialogOpen] = useState(false);
  const [referenceDialogOpen, setReferenceDialogOpen] = useState(false);
  const [alarmDialogOpen, setAlarmDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  // Form nuovo sensore
  const [newSensor, setNewSensor] = useState({
    name: '',
    description: '',
    tcp_host: '',
    tcp_port: 2940,
    initial_latitude: '',
    initial_longitude: '',
    initial_altitude: '',
  });

  // Form modifica sensore
  const [editSensorData, setEditSensorData] = useState({
    name: '',
    description: '',
    tcp_host: '',
    tcp_port: 2940,
  });

  // Form riferimento
  const [referenceData, setReferenceData] = useState({
    latitude: '',
    longitude: '',
    altitude: '',
  });

  // Configurazione allarmi
  const [alarmConfig, setAlarmConfig] = useState({
    delta_position: { warning: 20, alarm: 50, enabled: true },
    quality: { warning: 2, alarm: 4, enabled: true },
    battery: { warning: 30, alarm: 15, enabled: true },
    offline: { warning: 5, alarm: 15, enabled: true },
    velocity: { warning: 1, alarm: 5, enabled: true },
  });

  useEffect(() => {
    loadSensors();
  }, []);

  const loadSensors = async () => {
    try {
      const data = await sensorsService.getAll(true);
      setSensors(data);
    } catch (error) {
      console.error('Error loading sensors:', error);
    }
  };

  const handleAddSensor = async () => {
    try {
      await sensorsService.create({
        ...newSensor,
        initial_latitude: parseFloat(newSensor.initial_latitude) || null,
        initial_longitude: parseFloat(newSensor.initial_longitude) || null,
        initial_altitude: parseFloat(newSensor.initial_altitude) || null,
      });
      setSnackbar({ open: true, message: 'Sensore aggiunto con successo', severity: 'success' });
      setAddDialogOpen(false);
      setNewSensor({ name: '', description: '', tcp_host: '', tcp_port: 2940, initial_latitude: '', initial_longitude: '', initial_altitude: '' });
      loadSensors();
    } catch (error) {
      setSnackbar({ open: true, message: 'Errore durante l\'aggiunta del sensore', severity: 'error' });
    }
  };

  const handleUpdateSensor = async () => {
    if (!selectedSensor) return;
    try {
      await sensorsService.update(selectedSensor.id, editSensorData);
      setSnackbar({ open: true, message: 'Sensore aggiornato con successo', severity: 'success' });
      setEditSensorDialogOpen(false);
      loadSensors();
    } catch (error) {
      setSnackbar({ open: true, message: 'Errore durante l\'aggiornamento', severity: 'error' });
    }
  };

  const handleDeleteSensor = async (sensorId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo sensore?')) return;
    try {
      await sensorsService.delete(sensorId);
      setSnackbar({ open: true, message: 'Sensore eliminato', severity: 'success' });
      loadSensors();
    } catch (error) {
      setSnackbar({ open: true, message: 'Errore durante l\'eliminazione', severity: 'error' });
    }
  };


  const handleResetAllData = async () => {
    try {
      await sensorsService.resetAllData();
      setSnackbar({ open: true, message: "Tutti i dati azzerati", severity: "success" });
      setResetDialogOpen(false);
      loadSensors();
    } catch (error) {
      setSnackbar({ open: true, message: "Errore azzeramento", severity: "error" });
    }
  };

  const handleSetBaseline = async () => {
    if (!selectedSensor) return;
    const lat = parseFloat(String(referenceData.latitude).replace(',', '.'));
    const lon = parseFloat(String(referenceData.longitude).replace(',', '.'));
    const alt = parseFloat(String(referenceData.altitude).replace(',', '.')) || 0;

    if (isNaN(lat) || isNaN(lon)) {
      setSnackbar({ open: true, message: 'Inserisci coordinate valide', severity: 'warning' });
      return;
    }

    try {
      await sensorsService.setReference(selectedSensor.id, { latitude: lat, longitude: lon, altitude: alt });
      setSnackbar({ open: true, message: 'Posizione di riferimento impostata', severity: 'success' });
      setReferenceDialogOpen(false);
      loadSensors();
    } catch (error) {
      setSnackbar({ open: true, message: 'Errore durante l\'impostazione del riferimento', severity: 'error' });
    }
  };

  const handleSaveAlarmConfig = async () => {
    if (!selectedSensor) return;
    try {
      for (const [type, config] of Object.entries(alarmConfig)) {
        await sensorsService.updateAlarmConfig(selectedSensor.id, type, {
          threshold_warning: type === 'delta_position' || type === 'velocity' ? config.warning / 1000 : config.warning,
          threshold_alarm: type === 'delta_position' || type === 'velocity' ? config.alarm / 1000 : config.alarm,
          enabled: config.enabled,
        });
      }
      setSnackbar({ open: true, message: 'Configurazione salvata', severity: 'success' });
      setAlarmDialogOpen(false);
    } catch (error) {
      setSnackbar({ open: true, message: 'Errore durante il salvataggio', severity: 'error' });
    }
  };

  const openEditSensorDialog = (sensor) => {
    setSelectedSensor(sensor);
    setEditSensorData({ name: sensor.name, description: sensor.description || '', tcp_host: sensor.tcp_host, tcp_port: sensor.tcp_port });
    setEditSensorDialogOpen(true);
  };

  const openBaselineDialog = (sensor) => {
    setSelectedSensor(sensor);
    setReferenceData({
      latitude: sensor.initial_latitude || '',
      longitude: sensor.initial_longitude || '',
      altitude: sensor.initial_altitude || '',
    });
    setReferenceDialogOpen(true);
  };

  const openAlarmConfigDialog = (sensor) => {
    setSelectedSensor(sensor);
    if (sensor.alarm_configs) {
      const config = {};
      sensor.alarm_configs.forEach((ac) => {
        config[ac.type] = {
          warning: ac.type === 'delta_position' || ac.type === 'velocity' ? (ac.threshold_warning || 0) * 1000 : ac.threshold_warning || 0,
          alarm: ac.type === 'delta_position' || ac.type === 'velocity' ? (ac.threshold_alarm || 0) * 1000 : ac.threshold_alarm || 0,
          enabled: ac.enabled,
        };
      });
      setAlarmConfig((prev) => ({ ...prev, ...config }));
    }
    setAlarmDialogOpen(true);
  };

  const getStatusColor = (status) => {
    const s = status?.toUpperCase();
    if (s === 'ONLINE') return 'success';
    if (s === 'WARNING') return 'warning';
    if (s === 'ALARM') return 'error';
    return 'default';
  };

  return (
    <Box sx={{ p: 2, pt: 8, height: '100%', overflow: 'auto' }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6">
              <RouterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Sensori configurati
            </Typography>
            <Box>
              <IconButton onClick={loadSensors} size="small">
                <RefreshIcon />
              </IconButton>
            <Tooltip title="Azzera tutti i dati">
              <IconButton onClick={() => setResetDialogOpen(true)} size="small" color="error">
                <ResetIcon />
              </IconButton>
            </Tooltip>
              {isAdmin && (
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  size="small"
                  onClick={() => setAddDialogOpen(true)}
                >
                  {isMobile ? '' : 'Aggiungi'}
                </Button>
              )}
            </Box>
          </Box>

          <List dense>
            {[...sensors].sort((a, b) => a.name.localeCompare(b.name)).map((sensor) => (
              <ListItem
                key={sensor.id}
                sx={{ borderRadius: 1, mb: 0.5, bgcolor: 'action.hover' }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight={500}>{sensor.name}</Typography>
                      <Chip
                        label={sensor.status?.toUpperCase() || 'OFFLINE'}
                        size="small"
                        color={getStatusColor(sensor.status)}
                        sx={{ height: 20, fontSize: 10 }}
                      />
                      {sensor.battery_level !== undefined && (
                        <Chip
                          label={sensor.status?.toLowerCase() === 'offline' ? '---' : `${sensor.battery_level}%`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: 10 }}
                        />
                      )}
                    </Box>
                  }
                  secondary={sensor.description || `${sensor.tcp_host}:${sensor.tcp_port}`}
                />
                {isAdmin && (
                  <ListItemSecondaryAction>
                    <IconButton size="small" onClick={() => openEditSensorDialog(sensor)} title="Modifica">
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => openAlarmConfigDialog(sensor)} title="Configura allarmi">
                      <WarningIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => openBaselineDialog(sensor)} title="Imposta riferimento">
                      <MyLocationIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteSensor(sensor.id)} color="error" title="Elimina">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            ))}
            {sensors.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                Nessun sensore configurato
              </Typography>
            )}
          </List>
        </CardContent>
      </Card>

      {/* Dialog aggiunta sensore */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Aggiungi nuovo sensore</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Nome sensore" value={newSensor.name} onChange={(e) => setNewSensor({ ...newSensor, name: e.target.value })} placeholder="es. GNSS-001" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Descrizione" value={newSensor.description} onChange={(e) => setNewSensor({ ...newSensor, description: e.target.value })} />
            </Grid>
            <Grid item xs={8}>
              <TextField fullWidth label="Host TCP" value={newSensor.tcp_host} onChange={(e) => setNewSensor({ ...newSensor, tcp_host: e.target.value })} placeholder="es. 192.168.1.100" />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth type="number" label="Porta" value={newSensor.tcp_port} onChange={(e) => setNewSensor({ ...newSensor, tcp_port: parseInt(e.target.value) })} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth type="number" label="Latitudine" value={newSensor.initial_latitude} onChange={(e) => setNewSensor({ ...newSensor, initial_latitude: e.target.value })} inputProps={{ step: 0.000001 }} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth type="number" label="Longitudine" value={newSensor.initial_longitude} onChange={(e) => setNewSensor({ ...newSensor, initial_longitude: e.target.value })} inputProps={{ step: 0.000001 }} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth type="number" label="Altitudine" value={newSensor.initial_altitude} onChange={(e) => setNewSensor({ ...newSensor, initial_altitude: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleAddSensor} disabled={!newSensor.name || !newSensor.tcp_host}>Aggiungi</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog modifica sensore */}
      <Dialog open={editSensorDialogOpen} onClose={() => setEditSensorDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Modifica sensore - {selectedSensor?.name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Nome sensore" value={editSensorData.name} onChange={(e) => setEditSensorData({ ...editSensorData, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Descrizione" value={editSensorData.description} onChange={(e) => setEditSensorData({ ...editSensorData, description: e.target.value })} />
            </Grid>
            <Grid item xs={8}>
              <TextField fullWidth label="Host TCP" value={editSensorData.tcp_host} onChange={(e) => setEditSensorData({ ...editSensorData, tcp_host: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth type="number" label="Porta" value={editSensorData.tcp_port} onChange={(e) => setEditSensorData({ ...editSensorData, tcp_port: parseInt(e.target.value) })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditSensorDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleUpdateSensor} startIcon={<SaveIcon />}>Salva</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog riferimento */}
      <Dialog open={referenceDialogOpen} onClose={() => setReferenceDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Posizione di riferimento - {selectedSensor?.name}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>Gli spostamenti verranno calcolati rispetto a questa posizione.</Alert>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth type="number" label="Latitudine (°)" value={referenceData.latitude} onChange={(e) => setReferenceData({ ...referenceData, latitude: e.target.value })} inputProps={{ step: 0.00000001 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth type="number" label="Longitudine (°)" value={referenceData.longitude} onChange={(e) => setReferenceData({ ...referenceData, longitude: e.target.value })} inputProps={{ step: 0.00000001 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth type="number" label="Altitudine (m)" value={referenceData.altitude} onChange={(e) => setReferenceData({ ...referenceData, altitude: e.target.value })} />
            </Grid>
          </Grid>
          {selectedSensor && (selectedSensor.current_latitude || selectedSensor.initial_latitude) && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">Posizione attuale:</Typography>
              <Typography variant="body2">
                {(selectedSensor.current_latitude || selectedSensor.initial_latitude)?.toFixed(8)}°, {(selectedSensor.current_longitude || selectedSensor.initial_longitude)?.toFixed(8)}°
              </Typography>
              <Button size="small" sx={{ mt: 1 }} onClick={() => setReferenceData({
                latitude: selectedSensor.current_latitude || selectedSensor.initial_latitude || '',
                longitude: selectedSensor.current_longitude || selectedSensor.initial_longitude || '',
                altitude: selectedSensor.current_altitude || selectedSensor.initial_altitude || '',
              })}>Usa posizione attuale</Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReferenceDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleSetBaseline} startIcon={<MyLocationIcon />}>Imposta</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog configurazione allarmi */}
      <Dialog open={alarmDialogOpen} onClose={() => setAlarmDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Configurazione allarmi - {selectedSensor?.name}</DialogTitle>
        <DialogContent>
          {Object.entries({
            delta_position: { label: 'Spostamento', unit: 'mm', max: 100 },
            quality: { label: 'Qualità GNSS (PDOP)', unit: '', max: 10 },
            battery: { label: 'Batteria', unit: '%', max: 100 },
            offline: { label: 'Offline', unit: 'min', max: 60 },
            velocity: { label: 'Velocità', unit: 'mm/giorno', max: 20 },
          }).map(([key, config]) => (
            <Box key={key} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2">{config.label}</Typography>
                <FormControlLabel
                  control={<Switch checked={alarmConfig[key]?.enabled ?? true} onChange={(e) => setAlarmConfig({ ...alarmConfig, [key]: { ...alarmConfig[key], enabled: e.target.checked } })} size="small" />}
                  label="Attivo"
                />
              </Box>
              <Box sx={{ px: 2 }}>
                <Typography variant="caption" color="text.secondary">Warning: {alarmConfig[key]?.warning || 0} {config.unit}</Typography>
                <Slider value={alarmConfig[key]?.warning || 0} onChange={(e, val) => setAlarmConfig({ ...alarmConfig, [key]: { ...alarmConfig[key], warning: val } })} max={config.max} valueLabelDisplay="auto" color="warning" disabled={!alarmConfig[key]?.enabled} />
                <Typography variant="caption" color="text.secondary">Allarme: {alarmConfig[key]?.alarm || 0} {config.unit}</Typography>
                <Slider value={alarmConfig[key]?.alarm || 0} onChange={(e, val) => setAlarmConfig({ ...alarmConfig, [key]: { ...alarmConfig[key], alarm: val } })} max={config.max} valueLabelDisplay="auto" color="error" disabled={!alarmConfig[key]?.enabled} />
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlarmDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleSaveAlarmConfig} startIcon={<SaveIcon />}>Salva</Button>
        </DialogActions>
      </Dialog>


      {/* Dialog Reset */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Conferma Azzeramento</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>Questa azione cancellerà tutti gli spostamenti e allarmi!</Alert>
          <Typography>Sei sicuro? Azione non reversibile.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" color="error" onClick={handleResetAllData}>Azzera Tutto</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default Sensors;
